import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';

import { FourDotFiveDateFormatPipe } from '../pipes/four-dot-five-date-format.pipe';
import { OverrideNoteDetail } from 'src/app/public/app/models/overrideNoteDetail';
import { AngularModalService } from './modal.service';
import { GenericService } from './generic.service';
import { StorageService } from './storage.service';
import { ModalConfig } from 'src/app/core/models/modalConfig';
import { OverrideCandidateSuspensionComponent } from 'src/app/shared/override-candidate-suspension/override-candidate-suspension.component';
import { UserNotificationsActionsModalComponent } from 'src/app/shared/user-notifications/user-notifications-actions-modal/user-notifications-actions-modal.component';

declare var bootbox: any;

@Injectable({
  providedIn: 'root'
})
export class SharedAjsService {

  private fourDotFiveDateFormatPipe = new FourDotFiveDateFormatPipe();

  private assessmentsSyncMessageListener = new Subject<any>();
  private refreshJobMatchesListener = new Subject<any>();

  constructor(
    @Inject(BsModalService) private bsModalService: BsModalService,
    @Inject(AngularModalService) private modalService: AngularModalService,
    @Inject(GenericService) private genericService: GenericService,
    @Inject(StorageService) private StorageService: StorageService
  ) { }

  schedulePhoneScreenOrInterview(type, scheduleData, successCallback, errorCallback) {
    let url = '';
    if (type == 'recruiterScreening') {
      url = 'api/candidate/schedulerecruiterscreening';
    } else if (type == 'phoneScreen') {
      url = 'api/candidate/schedulephonescreen';
    } else if (type == 'interview') {
      url = 'api/candidate/scheduleinterview';
    }
    this.genericService.addObject(this.StorageService.get('baseurl') + url, scheduleData).subscribe((data) => {
      successCallback(data.body);
    }, (error) => {
      errorCallback(error.error);
    });
  }

  rescheduleMeeting(meetingId, meetingObject, successCallback, errorCallback) {
    this.genericService.addObject(this.StorageService.get('baseurl') + 'api/candidate/reschedulemeeting' + '/' + meetingId, meetingObject).subscribe((data) => {
      successCallback(data.body);
    }, (error) => {
      errorCallback(error.error);
    });
  }

  // Assessments Sync message
  receiveAssessmentsSyncMessage() {
    return this.assessmentsSyncMessageListener.asObservable();
  }

  displayAssessmentsSyncMessage(htmlText) {
    this.assessmentsSyncMessageListener.next(htmlText);
  }

  // shared methods
  onAssociateToJobClick(jobMatch, createJobMatchesObject, candidateName, canAssociateCallback, cannotAssociateCallback) {
    if (jobMatch.showOverride) {
      this._confirmOverrideSuspension(jobMatch, createJobMatchesObject, candidateName, canAssociateCallback, cannotAssociateCallback);
    } else if (jobMatch.allowManualMatch) {
      if (canAssociateCallback) {
        canAssociateCallback();
      }
    } else {
      this._showBootboxWarning(jobMatch.reasonForNoManualMatch);
    }
  }

  _showBootboxWarning(message) {
    bootbox.alert({
      closeButton: false,
      title: "<div class='alert alert-warning' style='margin-bottom: 0px;'><i class='fas fa-exclamation-triangle fa-fw fa-lg'></i><strong>Cannot associate candidate</strong></div>",
      message: message,
      className: "zIndex1060",
      backdrop: true,
      onEscape: false,
      buttons: {
        ok: {
          label: 'Ok',
          className: 'btn-info'
        }
      }, callback: () => {
        this.modalService.addModalOpenClassToBodyIfAnyModalIsOpen();
      }
    });
  }

  _confirmOverrideSuspension(candidate, createJobMatchesObject, candidateName, canAssociateCallback, cannotAssociateCallback) {
    let message = candidate.reasonForNoManualMatch;

    let type = "release";
    let allowOverride = candidate.allowOverride;
    let suspendedMonths = 0;
    let releaseDate = '';
    let releasedJobDetails: any = {};

    if (candidate.overrideFor === "JOB_LEFT") {
      type = "jobLeft";
      suspendedMonths = candidate.jobLeftDetail.numberOfMonthsToWait;
      releaseDate = this.fourDotFiveDateFormatPipe.transform(candidate.jobLeftDetail.leftAt);
      releasedJobDetails = candidate.jobLeftDetail;
    } else {
      suspendedMonths = candidate.releasedCandidateDetail.numberOfMonthsToWait;
      releaseDate = this.fourDotFiveDateFormatPipe.transform(candidate.releasedCandidateDetail.releasedAt);
      releasedJobDetails = candidate.releasedCandidateDetail;
    }
    let candidateId = releasedJobDetails.candidateId;

    if (allowOverride) {
      bootbox.confirm({
        closeButton: false,
        title: "<div class='alert alert-warning' style='margin-bottom: 0px;'><i class='fas fa-exclamation-triangle fa-fw fa-lg'></i><strong>Confirm</strong></div>",
        message: message,
        className: "zIndex1060",
        backdrop: true,
        onEscape: false,
        buttons: {
          confirm: {
            label: 'Override',
            className: 'btn-info'
          },
          cancel: {
            label: 'Cancel',
            className: 'btn-danger'
          }
        },
        callback: (result) => {
          if (result) {
            this.modalService.addModalOpenClassToBodyIfAnyModalIsOpen();
            const config = new ModalConfig();
            config.class = 'four-dot-five-modal-medium';
            config.initialState = {
              candidateId,
              candidateName,
              suspendedMonths,
              releaseDate,
              allowOverride,
              releasedJobDetails,
              type,
              recruiterDetails: new OverrideNoteDetail(candidateId)
            };
            const bsModalRef: BsModalRef = this.bsModalService.show(OverrideCandidateSuspensionComponent, config);
            bsModalRef.content.saveCallback.subscribe(() => {
              if (type === 'release') {
                candidate.releasedCandidateDetail.releasedAt = null;
              } else {
                candidate.jobLeftDetail.leftAt = null;
              }
              createJobMatchesObject.overrideNoteDetails.push(bsModalRef.content.recruiterDetails);
              if (canAssociateCallback) {
                canAssociateCallback();
              }
              bsModalRef.hide();
            });
            bsModalRef.content.cancelCallback.subscribe(() => {
              if (cannotAssociateCallback) {
                cannotAssociateCallback();
              }
              bsModalRef.hide();
            });
          } else {
            this.modalService.addModalOpenClassToBodyIfAnyModalIsOpen();
          }
        }
      });
    } else {
      let displayHtmlText = "";
      if (type == 'release') {
        displayHtmlText = "Candidate '<span class='emerald'>" + candidateName + "</span>' cannot apply for any position as it is within '<span class='emerald'>" + suspendedMonths + "</span>' month(s) of candidate's previous release. ";
        displayHtmlText = displayHtmlText + "Candidate was previously released on '<span class='emerald'>" + this.fourDotFiveDateFormatPipe.transform(releaseDate) + "</span>' from the role '<span class='emerald'>" + releasedJobDetails.role + "</span>' ";
        displayHtmlText = displayHtmlText + "with Requisition Number '<span class='emerald'>" + releasedJobDetails.requisitionNumber + "</span>'.";
      } else {
        displayHtmlText = "Candidate <span class='emerald'>'" + candidateName + "'</span> cannot apply for any position as it is within '<span class='emerald'>" + suspendedMonths + "</span>' month(s) of leaving previous job. ";
        displayHtmlText = displayHtmlText + "Candidate left previous job on '<span class='emerald'>" + this.fourDotFiveDateFormatPipe.transform(releaseDate) + "</span>' from the role '<span class='emerald'>" + releasedJobDetails.role + "</span>' ";
        displayHtmlText = displayHtmlText + "with Requisition Number '<span class='emerald'>" + releasedJobDetails.requisitionNumber + "</span>'.";
      }

      bootbox.alert({
        closeButton: false,
        title: "<div class='alert alert-warning' style='margin-bottom: 0px;'><i class='fas fa-exclamation-triangle fa-fw fa-lg'></i><strong>Cannot associate candidate</strong></div>",
        message: displayHtmlText,
        className: "zIndex1060",
        buttons: {
          ok: {
            label: 'Ok',
            className: 'btn-info'
          }
        },
        callback: () => {
          if (cannotAssociateCallback) {
            cannotAssociateCallback();
          }
          this.modalService.addModalOpenClassToBodyIfAnyModalIsOpen();
        }
      });
    }
  }

  setRefreshJobMatchesListener() {
    this.refreshJobMatchesListener = new Subject<any>();
  }

  raiseRefreshJobMatchesEvent(candidateId, jobMatchesActiveTab) {
    this.refreshJobMatchesListener.next({ candidateId: candidateId, jobMatchesActiveTab: jobMatchesActiveTab });
  }

  receiveRefreshJobMatchesEvent() {
    return this.refreshJobMatchesListener.asObservable();
  }

  resetRefreshJobMatchesListener() {
    this.refreshJobMatchesListener.complete();
    this.setRefreshJobMatchesListener();
  }

  goToCandidateDocuments(actionType, actionLabel, candidateObj) {
    let windowClass = 'four-dot-five-modal-large';
    if (actionType == 'emailNotification') {
      windowClass = 'four-dot-five-modal-medium';
    }
    const config = new ModalConfig();
    config.class = windowClass;
    config.keyboard = false;
    config.initialState = {
      actionType,
      actionLabel,
      documentDtoMap: candidateObj
    };
    this.bsModalService.show(UserNotificationsActionsModalComponent, config);
  }
}
